import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertTriangle, Shield, CheckCircle2 } from 'lucide-react';

const AgeVerificationModal = ({ isOpen, onVerified, onClose }) => {
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({});
  const [birthYear, setBirthYear] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [error, setError] = useState('');

  const questions = [
    {
      id: 'confirm_17',
      question: 'I confirm that I am at least 18 years of age',
      type: 'confirm'
    },
    {
      id: 'understand_skill_gaming',
      question: 'I understand that GEMS is a skill-based social gaming platform that uses virtual currency (Emeralds and Rubies)',
      type: 'confirm'
    },
    {
      id: 'accept_responsibility',
      question: 'I accept full responsibility for my participation and agree to follow the Community Guidelines',
      type: 'confirm'
    },
    {
      id: 'legal_jurisdiction',
      question: 'I confirm that online skill-based gaming is legal in my jurisdiction',
      type: 'confirm'
    }
  ];

  const validateAge = () => {
    const year = parseInt(birthYear);
    const month = parseInt(birthMonth);
    const day = parseInt(birthDay);
    
    if (!year || !month || !day) {
      setError('Please enter your complete date of birth');
      return false;
    }
    
    const today = new Date();
    const birthDate = new Date(year, month - 1, day);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    if (age < 18) {
      setError('You must be at least 18 years old to use this app');
      return false;
    }
    
    if (age > 120 || year < 1900) {
      setError('Please enter a valid date of birth');
      return false;
    }
    
    return true;
  };

  const handleNext = () => {
    if (step === 1) {
      if (!validateAge()) return;
      setStep(2);
      setError('');
    } else if (step === 2) {
      // Check all questions answered
      const allAnswered = questions.every(q => answers[q.id] === true);
      if (!allAnswered) {
        setError('Please confirm all statements to continue');
        return;
      }
      setStep(3);
      setError('');
    } else if (step === 3) {
      // Final confirmation
      onVerified({
        birthDate: `${birthYear}-${birthMonth.padStart(2, '0')}-${birthDay.padStart(2, '0')}`,
        verifiedAt: new Date().toISOString(),
        answers: answers
      });
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - 17 - i);
  const months = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];
  const days = Array.from({ length: 31 }, (_, i) => i + 1);

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-lg" hideClose>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Shield className="h-6 w-6 text-emerald-500" />
            Age Verification Required
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Progress indicator */}
          <div className="flex justify-center gap-2">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`w-3 h-3 rounded-full transition-colors ${
                  step >= s ? 'bg-emerald-500' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
          
          {step === 1 && (
            <div className="space-y-4">
              <div className="text-center">
                <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-3" />
                <h3 className="text-lg font-semibold">Verify Your Age</h3>
                <p className="text-gray-600 text-sm mt-1">
                  You must be at least 18 years old to use GEMS
                </p>
              </div>
              
              <div className="space-y-3">
                <label className="block text-sm font-medium">Date of Birth</label>
                <div className="grid grid-cols-3 gap-2">
                  <select
                    value={birthMonth}
                    onChange={(e) => setBirthMonth(e.target.value)}
                    className="w-full p-2 border rounded-lg bg-white"
                  >
                    <option value="">Month</option>
                    {months.map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                  
                  <select
                    value={birthDay}
                    onChange={(e) => setBirthDay(e.target.value)}
                    className="w-full p-2 border rounded-lg bg-white"
                  >
                    <option value="">Day</option>
                    {days.map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                  
                  <select
                    value={birthYear}
                    onChange={(e) => setBirthYear(e.target.value)}
                    className="w-full p-2 border rounded-lg bg-white"
                  >
                    <option value="">Year</option>
                    {years.map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              {error && (
                <p className="text-red-500 text-sm text-center">{error}</p>
              )}
            </div>
          )}
          
          {step === 2 && (
            <div className="space-y-4">
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold">Please Confirm</h3>
                <p className="text-gray-600 text-sm">Check each box to continue</p>
              </div>
              
              <div className="space-y-3">
                {questions.map((q) => (
                  <label
                    key={q.id}
                    className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={answers[q.id] || false}
                      onChange={(e) => setAnswers({...answers, [q.id]: e.target.checked})}
                      className="mt-1 h-5 w-5 text-emerald-600 rounded"
                    />
                    <span className="text-sm text-gray-700">{q.question}</span>
                  </label>
                ))}
              </div>
              
              {error && (
                <p className="text-red-500 text-sm text-center">{error}</p>
              )}
            </div>
          )}
          
          {step === 3 && (
            <div className="text-center space-y-4">
              <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto" />
              <h3 className="text-xl font-semibold text-emerald-600">Verification Complete!</h3>
              <p className="text-gray-600">
                Thank you for verifying your age. You may now proceed to create your account.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left">
                <p className="text-amber-800 text-sm">
                  <strong>Note:</strong> Additional identity verification (government ID) will be required when you request your first withdrawal.
                </p>
              </div>
            </div>
          )}
          
          <div className="flex gap-3">
            {step > 1 && step < 3 && (
              <Button
                variant="outline"
                onClick={() => setStep(step - 1)}
                className="flex-1"
              >
                Back
              </Button>
            )}
            <Button
              onClick={handleNext}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
            >
              {step === 3 ? 'Continue to Sign Up' : 'Continue'}
            </Button>
          </div>
          
          <p className="text-xs text-gray-500 text-center">
            By continuing, you confirm that all information provided is accurate.
            Providing false information may result in account termination.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AgeVerificationModal;
